import { doc, setDoc } from 'firebase/firestore';

import { isAnalyticsEnabled } from 'services/analytics/analyticsConfig';
import { auth, firestore } from 'services/firebase/client';

import type {
  JourneyDeviceType,
  JourneyEvent,
  JourneyPageVisit,
  JourneySession,
  JourneyStatus,
} from './journeyTypes';

const COLLECTION = 'journey_sessions';
const SESSION_STORAGE_KEY = 'analytics_session_id';
const SESSION_BUFFER_KEY = 'journey_session_buffer';
const FLUSH_DEBOUNCE_MS = 1500;
const MAX_PAGES = 80;
const MAX_EVENTS = 150;

const IGNORED_PATH_PREFIXES = ['/dashboard'];

let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushInFlight: Promise<void> | null = null;
let hasWarnedPermission = false;

const createSessionId = (): string => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const nowIso = (): string => new Date().toISOString();

const readQueryParam = (key: string): string | undefined => {
  try {
    const value = new URLSearchParams(window.location.search).get(key);
    return value?.trim() || undefined;
  } catch {
    return undefined;
  }
};

const shouldTrackPath = (path: string): boolean => {
  return !IGNORED_PATH_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`),
  );
};

const stripUndefined = <T extends Record<string, unknown>>(obj: T): T => {
  const next: Record<string, unknown> = {};
  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined) {
      next[key] = value;
    }
  });
  return next as T;
};

const loadBuffer = (): JourneySession | null => {
  try {
    const raw = sessionStorage.getItem(SESSION_BUFFER_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as JourneySession;
  } catch {
    return null;
  }
};

const saveBuffer = (session: JourneySession): void => {
  try {
    sessionStorage.setItem(SESSION_BUFFER_KEY, JSON.stringify(session));
  } catch {
    // Ignore quota / private mode failures.
  }
};

const detectDeviceType = (userAgent: string): JourneyDeviceType => {
  const uaData = (
    navigator as Navigator & {
      userAgentData?: { mobile?: boolean };
    }
  ).userAgentData;

  if (uaData?.mobile === true) {
    return 'mobile';
  }

  // iPadOS 13+ may report as Macintosh; treat touch Macs with no hover as tablet-ish mobile.
  const isIpadOsDesktopUa =
    /Macintosh/i.test(userAgent) &&
    typeof navigator.maxTouchPoints === 'number' &&
    navigator.maxTouchPoints > 1;

  if (
    isIpadOsDesktopUa ||
    /iPad|Tablet|PlayBook|Silk|(Android(?!.*Mobile))/i.test(userAgent)
  ) {
    return 'tablet';
  }

  if (
    /Mobi|Android|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent,
    )
  ) {
    return 'mobile';
  }

  return 'desktop';
};

class JourneySessionService {
  private session: JourneySession | null = null;

  getSessionId(): string {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const sessionId = createSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, sessionId);
    return sessionId;
  }

  private ensureSession(entryPath: string): JourneySession {
    if (this.session) {
      return this.session;
    }

    const buffered = loadBuffer();
    if (buffered?.sessionId) {
      this.session = buffered;
      if (!this.session.deviceType) {
        const ua = this.session.userAgent || navigator.userAgent || '';
        this.session.deviceType = detectDeviceType(ua);
        this.session.userAgent =
          this.session.userAgent || ua.slice(0, 180) || undefined;
        saveBuffer(this.session);
      }
      sessionStorage.setItem(SESSION_STORAGE_KEY, buffered.sessionId);
      return this.session;
    }

    const sessionId = this.getSessionId();
    const userAgent = navigator.userAgent?.slice(0, 180);
    this.session = {
      sessionId,
      startedAt: nowIso(),
      updatedAt: nowIso(),
      entryPath,
      referrer: document.referrer || undefined,
      utmSource: readQueryParam('utm_source'),
      utmMedium: readQueryParam('utm_medium'),
      utmCampaign: readQueryParam('utm_campaign'),
      userId: auth.currentUser?.uid ?? null,
      status: 'active',
      pages: [],
      events: [],
      userAgent,
      deviceType: detectDeviceType(userAgent || ''),
    };
    saveBuffer(this.session);
    return this.session;
  }

  private closeOpenPage(leftAt: string): void {
    if (!this.session || this.session.pages.length === 0) {
      return;
    }

    const lastPage = this.session.pages[this.session.pages.length - 1];
    if (lastPage.leftAt) {
      return;
    }

    const enteredMs = Date.parse(lastPage.enteredAt);
    const leftMs = Date.parse(leftAt);
    lastPage.leftAt = leftAt;
    lastPage.durationSeconds = Number.isFinite(enteredMs)
      ? Math.max(0, Math.round((leftMs - enteredMs) / 1000))
      : 0;
  }

  private touch(status?: JourneyStatus): void {
    if (!this.session) {
      return;
    }
    this.session.updatedAt = nowIso();
    if (
      status &&
      !(this.session.status === 'completed' && status === 'active')
    ) {
      this.session.status = status;
    }
    if (auth.currentUser?.uid) {
      this.session.userId = auth.currentUser.uid;
    }
    saveBuffer(this.session);
    this.scheduleFlush();
  }

  trackPageView(path: string, title?: string): void {
    if (!isAnalyticsEnabled()) {
      return;
    }

    if (!shouldTrackPath(path)) {
      return;
    }

    const at = nowIso();
    const session = this.ensureSession(path);
    const lastPage = session.pages[session.pages.length - 1];

    // Avoid duplicate consecutive page_views (hook + page-level trackPageView).
    if (lastPage && !lastPage.leftAt && lastPage.path === path) {
      if (title && !lastPage.title) {
        lastPage.title = title;
        this.touch('active');
      }
      return;
    }

    this.closeOpenPage(at);

    if (session.pages.length >= MAX_PAGES) {
      session.pages.shift();
    }

    const visit: JourneyPageVisit = {
      path,
      title,
      enteredAt: at,
    };
    session.pages.push(visit);
    session.exitPath = path;
    this.touch('active');
  }

  trackEvent(
    type: JourneyEvent['type'],
    id: string,
    path: string,
    label?: string,
    meta?: JourneyEvent['meta'],
  ): void {
    if (!isAnalyticsEnabled()) {
      return;
    }

    if (!shouldTrackPath(path)) {
      return;
    }

    const session = this.ensureSession(path);
    if (session.events.length >= MAX_EVENTS) {
      session.events.shift();
    }

    session.events.push(
      stripUndefined({
        type,
        id,
        path,
        at: nowIso(),
        label,
        meta,
      }) as JourneyEvent,
    );
    this.touch('active');
  }

  trackCta(
    ctaId: string,
    path: string,
    label?: string,
    meta?: JourneyEvent['meta'],
  ): void {
    this.trackEvent('cta_click', ctaId, path, label, meta);
  }

  trackFunnelStep(
    step: string,
    path: string,
    meta?: JourneyEvent['meta'],
  ): void {
    this.trackEvent('funnel_step', step, path, step, meta);
  }

  endSession(status: JourneyStatus = 'abandoned'): void {
    if (!isAnalyticsEnabled()) {
      return;
    }

    if (!this.session) {
      const buffered = loadBuffer();
      if (!buffered) {
        return;
      }
      this.session = buffered;
    }

    const at = nowIso();
    this.closeOpenPage(at);
    this.session.endedAt = at;
    this.session.exitPath =
      this.session.pages[this.session.pages.length - 1]?.path ??
      this.session.entryPath;
    this.session.totalDurationSeconds = Math.max(
      0,
      Math.round(
        (Date.parse(at) - Date.parse(this.session.startedAt)) / 1000,
      ),
    );
    this.session.status = status;
    this.session.updatedAt = at;
    saveBuffer(this.session);
    void this.flush({ immediate: true });
  }

  markCompleted(): void {
    this.endSession('completed');
  }

  private scheduleFlush(): void {
    if (flushTimer) {
      clearTimeout(flushTimer);
    }
    flushTimer = setTimeout(() => {
      flushTimer = null;
      void this.flush();
    }, FLUSH_DEBOUNCE_MS);
  }

  async flush(options?: { immediate?: boolean }): Promise<void> {
    if (flushTimer && options?.immediate) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }

    if (!this.session) {
      return;
    }

    if (flushInFlight) {
      await flushInFlight;
    }

    const payload = { ...this.session };
    flushInFlight = this.persist(payload).finally(() => {
      flushInFlight = null;
    });
    await flushInFlight;
  }

  private async persist(session: JourneySession): Promise<void> {
    try {
      const ref = doc(firestore, COLLECTION, session.sessionId);
      await setDoc(
        ref,
        stripUndefined({
          ...session,
          userId: session.userId ?? auth.currentUser?.uid ?? null,
        }),
        { merge: true },
      );
    } catch (error) {
      const message =
        typeof error === 'object' && error && 'message' in error
          ? String((error as { message: string }).message)
          : '';
      const code =
        typeof error === 'object' && error && 'code' in error
          ? String((error as { code: string }).code)
          : '';

      if (
        code === 'permission-denied' ||
        message.includes('Missing or insufficient permissions')
      ) {
        if (!hasWarnedPermission) {
          hasWarnedPermission = true;
          console.warn(
            'Journey session: sem permissão para gravar no Firestore. Atualize as firestore.rules.',
          );
        }
        return;
      }

      console.error('Journey session: falha ao persistir', error);
    }
  }
}

export const journeySessionService = new JourneySessionService();
export default journeySessionService;
